import Clipboard from 'clipboard';
import { useSession } from 'next-auth/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EmailIcon,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import styled, { css } from 'styled-components';

import theme from '../../../assets/theme';
import { useMetrics } from '../../../hooks/useMetrics';
import { useMarketingMetrics } from '../../../providers/PlausibleAnalyticsProvider';
import { getBaseUrl } from '../../../utils/client';
import { Input } from '../../_atoms/_Input';
import MSTeamsShareButton from '../MSTeamsShareButton';
import QRCodeShareButton from '../QRCodeShareButton';

const ShareGlobalLesson = ({
  templateId,
  isGeneratedByAI,
  isGenerator = false,
  title,
}: {
  templateId: string;
  isGeneratedByAI: boolean;
  isGenerator?: boolean;
  title?: string;
}) => {
  const session = useSession();
  const templateRef = useRef(new Clipboard('#copy_url'));
  const metrics = useMetrics();
  const { track } = useMarketingMetrics();
  const [copySuccess, setCopySuccess] = useState(false);
  const { t } = useTranslation('Host');
  useEffect(() => {
    const clip = templateRef.current;

    clip.on('success', () => {
      if (session.status === 'authenticated') {
        metrics.logEvent('Share.Lesson.CopyLink', {
          templateId: !isGenerator ? templateId : null,
          isGenerator,
          generatorId: isGenerator ? templateId : null,
        });
      } else {
        track('Share.Lesson.CopyLink', {
          props: {
            templateId: !isGenerator ? templateId : null,
            isGenerator,
            generatorId: isGenerator ? templateId : null,
          },
        });
      }
      setCopySuccess(true);
    });

    return () => {
      clip.destroy();
    };
  }, [metrics, templateId, session.status, track, isGenerator]);

  useEffect(() => {
    const ref = setTimeout(() => {
      if (copySuccess) {
        setCopySuccess(false);
      }
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [copySuccess]);

  const shareUrl = isGenerator
    ? `${getBaseUrl()}/generators/${(title || '')
        .replace(/\s/g, '-')
        .replace(/[^\w\s\-]/gi, '')}/g/${templateId}`
    : `${getBaseUrl()}/discover/${templateId}`;

  const shareTitle = isGeneratedByAI
    ? `${t('This lesson was generated by AI in less than 10 seconds')}`
    : '\n';

  const emailSubject = isGeneratedByAI
    ? t('Generate lessons with in 10 seconds')
    : t('Check out my this Curipod lesson');

  const handleClickShareButton = (type: string) => {
    if (session.status === 'authenticated') {
      metrics.logEvent('Share click', { someType: type });
    } else {
      track('Share click', { props: { someType: type } });
    }
  };

  return (
    <div className={'globalShareModal'}>
      <p style={{ fontWeight: 'bold' }}>
        {t('Copy the link below and share it with your colleague')}
      </p>
      <Title $fontSize={18}>Link</Title>

      <SocialMediaContainer>
        <TwitterShareButton
          url={shareUrl}
          title={shareTitle}
          onClick={() => handleClickShareButton('twitter')}
        >
          <TwitterIcon size={32} round={true} />
        </TwitterShareButton>
        <LinkedinShareButton
          url={shareUrl}
          title={shareTitle}
          onClick={() => handleClickShareButton('linkedin')}
        >
          <LinkedinIcon size={32} round={true} />
        </LinkedinShareButton>
        <FacebookShareButton
          url={shareUrl}
          quote={shareTitle}
          onClick={() => handleClickShareButton('facebook')}
        >
          <FacebookIcon size={32} round={true} />
        </FacebookShareButton>
        <QRCodeShareButton url={shareUrl} />
        <a
          href={`mailto:?subject=${emailSubject}&body=${t(
            'Below is a link to a Curipod lesson.',
          )}\n\n\n ${shareUrl}`}
          onClick={() => handleClickShareButton('email')}
        >
          <EmailIcon size={32} round={true} />
        </a>
        <WhatsappShareButton
          url={shareUrl}
          title={shareTitle}
          onClick={() => handleClickShareButton('whatsapp')}
        >
          <WhatsappIcon size={32} round={true} />
        </WhatsappShareButton>
        <MSTeamsShareButton url={shareUrl} body={shareTitle} />
      </SocialMediaContainer>

      <JoinLinkContainer>
        <InputContainer
          role="button"
          id="copy_url"
          data-clipboard-text={shareUrl}
          $isCopied={copySuccess}
        >
          <Input style={{ flex: '1' }} value={shareUrl} />
        </InputContainer>
      </JoinLinkContainer>
    </div>
  );
};

export default ShareGlobalLesson;

const SocialMediaContainer = styled.div`
  display: flex;
  padding-bottom: 0.5em;
  & > *:not(:first-child) {
    margin-left: 2px;
  }
  & > *:not(:last-child) {
    margin-right: 2px;
  }
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;

const JoinLinkContainer = styled.div`
  display: flex;

  @media (max-width: 690px) {
    flex-direction: column;

    button {
      margin-top: 10px;
      max-width: max-content;
      align-self: center;
    }
  }
`;
const InputContainer = styled.div<{ $isCopied: boolean }>`
  display: flex;
  cursor: pointer;
  position: relative;
  width: 100%;
  margin-right: 5px;

  input {
    cursor: pointer;
  }

  &:after {
    ${({ $isCopied }) =>
      $isCopied
        ? css`
            content: 'Copied';
          `
        : css`
            content: 'Copy link';
          `};
    position: absolute;
    right: 3px;
    top: 3px;
    font-weight: 700;
    background: #fff;
    padding: 7px;
  }

  @media (max-width: 690px) {
    input {
      cursor: pointer;
      font-size: 16px;
    }

    &:after {
      font-size: 16px;
      top: 3px;
    }
  }
`;
